import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { _Api } from "../api";
import "./CregisPayment.scss";

const POLL_INTERVAL_MS = 5000;
const DEFAULT_COUNTDOWN_SEC = 1800;
const QUERY_ORDER_EVERY_POLLS = 3;

const STATUS = {
  NEW: "new",
  PAID: "paid",
  PAID_OVER: "paid_over",
  PAID_PARTIAL: "paid_partial",
  EXPIRED: "expired",
  CANCELED: "canceled",
};

const resolveStatus = (data = {}) =>
  String(
    data?.status || data?.payment_status || data?.order_status || STATUS.NEW
  ).toLowerCase();

const isPaymentSuccess = (data = {}) => {
  if (data?.payment_success === true) return true;
  const status = resolveStatus(data);
  return status === STATUS.PAID || status === STATUS.PAID_OVER;
};

const isTerminalFailure = (data = {}) => {
  const status = resolveStatus(data);
  return status === STATUS.EXPIRED || status === STATUS.CANCELED;
};

const formatCountdown = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const statusLabel = (status) => {
  const s = String(status || STATUS.NEW).toLowerCase();
  if (s === STATUS.PAID) return "Paid";
  if (s === STATUS.PAID_OVER) return "Overpaid";
  if (s === STATUS.PAID_PARTIAL) return "Partial payment";
  if (s === STATUS.EXPIRED) return "Expired";
  if (s === STATUS.CANCELED) return "Canceled";
  return "Awaiting payment";
};

const openCheckoutUrl = (url) => {
  if (!url) return null;
  return window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Ecommerce Cregis: create order → auto-open checkout_url →
 * poll backend status (webhook is source of truth)
 */
const EcommerceCregisPayment = ({
  userdata,
  payamount,
  paymentdatashow,
  setCreditpaymentdata,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [message, setMessage] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [cregisId, setCregisId] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [countdown, setCountdown] = useState(DEFAULT_COUNTDOWN_SEC);
  const [orderStatus, setOrderStatus] = useState(STATUS.NEW);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const transactionIdRef = useRef("");
  const cregisIdRef = useRef("");
  const pollCountRef = useRef(0);
  const timerRef = useRef(null);
  const completedRef = useRef(false);
  const checkoutWindowRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setPolling(false);
  }, []);

  const closeCheckoutWindow = useCallback(() => {
    try {
      if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
        checkoutWindowRef.current.close();
      }
    } catch (err) {
      // ignore
    }
    checkoutWindowRef.current = null;
  }, []);

  const markPaid = useCallback(
    (data = {}) => {
      if (completedRef.current) return;
      completedRef.current = true;
      stopPolling();
      closeCheckoutWindow();

      const paidId =
        data?.cregis_id ||
        data?.transactionID ||
        data?.payment_intent_id ||
        cregisIdRef.current ||
        transactionIdRef.current;

      setOrderStatus(resolveStatus(data) || STATUS.PAID);
      setMessage(t("Payment verified by Cregis. Completing order..."));
      setCreditpaymentdata((state) => ({
        ...state,
        payment: true,
        message: "",
        submitdata: false,
        stripekey: paidId,
        Paymentmethod_type: paymentdatashow?.Paymentmethod_type,
        payment_slag: paymentdatashow?.payment_slag || "cregis-payment",
        id: paymentdatashow?.id,
        PaymentMethod: paymentdatashow?.PaymentMethod,
        PaymentMethod_id:
          paymentdatashow?.PaymentMethod_id || paymentdatashow?.id,
      }));
    },
    [closeCheckoutWindow, paymentdatashow, setCreditpaymentdata, stopPolling, t]
  );

  const checkPaymentStatus = useCallback(async () => {
    const txId = transactionIdRef.current;
    if (!txId || completedRef.current) return;

    try {
      const response = await _Api(
        {
          transactionId: txId,
          payment_slag: paymentdatashow?.payment_slag || "cregis-payment",
          cregis_id: cregisIdRef.current || undefined,
        },
        `checkout/get-payment-status`
      );

      if (response?.statusCode !== 200 && response?.code !== 200) {
        setMessage(response?.message || t("Unable to check payment status"));
        return;
      }

      const data = response?.data || {};
      const status = resolveStatus(data);
      setOrderStatus(status);

      if (data?.cregis_id) {
        cregisIdRef.current = data.cregis_id;
        setCregisId(data.cregis_id);
      }

      if (isPaymentSuccess(data)) {
        markPaid(data);
        return;
      }

      if (isTerminalFailure(data)) {
        stopPolling();
        closeCheckoutWindow();
        setMessage(
          status === STATUS.EXPIRED
            ? t("Payment expired. Please create a new order.")
            : t("Payment canceled.")
        );
        return;
      }

      if (status === STATUS.PAID_PARTIAL) {
        setMessage(
          t("Partial payment received. Please complete payment on Cregis checkout.")
        );
      } else {
        setMessage(
          t("Waiting for Cregis webhook confirmation. Pay on the checkout page.")
        );
      }

      pollCountRef.current += 1;
      if (
        pollCountRef.current % QUERY_ORDER_EVERY_POLLS === 0 &&
        cregisIdRef.current
      ) {
        try {
          const queryRes = await _Api(
            {
              cregis_id: cregisIdRef.current,
              transactionId: txId,
              payment_slag: paymentdatashow?.payment_slag || "cregis-payment",
            },
            `checkout/cregis/query-order`
          );
          const queryData = queryRes?.data || {};
          if (
            (queryRes?.statusCode === 200 || queryRes?.code === 200) &&
            isPaymentSuccess(queryData)
          ) {
            markPaid(queryData);
          } else if (queryData?.status) {
            setOrderStatus(resolveStatus(queryData));
          }
        } catch (queryErr) {
          console.warn("Cregis query-order optional call failed", queryErr);
        }
      }
    } catch (err) {
      console.error("Ecommerce Cregis status error:", err);
    }
  }, [
    closeCheckoutWindow,
    markPaid,
    paymentdatashow?.payment_slag,
    stopPolling,
    t,
  ]);

  const startPolling = useCallback(() => {
    stopPolling();
    setPolling(true);
    pollCountRef.current = 0;
    checkPaymentStatus();

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopPolling();
          closeCheckoutWindow();
          setMessage(t("Payment timeout. Please try again."));
          return 0;
        }
        return prev - 1;
      });
      checkPaymentStatus();
    }, POLL_INTERVAL_MS);
  }, [checkPaymentStatus, closeCheckoutWindow, stopPolling, t]);

  const reopenCheckout = () => {
    if (!checkoutUrl) return;
    const win = openCheckoutUrl(checkoutUrl);
    if (win) {
      checkoutWindowRef.current = win;
      setPopupBlocked(false);
    } else {
      setPopupBlocked(true);
    }
  };

  const createCregisOrder = async () => {
    const email = userdata?.billingaddress?.emails?.[0];
    const name =
      userdata?.billingaddress?.fname || userdata?.billingaddress?.name;

    if (!email || !name) {
      setMessage(t("Billing name and email are required"));
      return;
    }
    if (!payamount || Number(payamount) <= 0) {
      setMessage(t("Amount is not Valid"));
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      setPopupBlocked(false);
      completedRef.current = false;
      stopPolling();
      closeCheckoutWindow();

      const ObjectPost = {
        amount: payamount,
        name,
        slug: paymentdatashow?.payment_slag || "cregis-payment",
        email,
        PaymentMethod: paymentdatashow?.PaymentMethod,
      };

      const res = await _Api(ObjectPost, `checkout/payment-token`);
      if (res?.statusCode !== 200 && res?.code !== 200) {
        setMessage(res?.message || t("Failed to create crypto payment"));
        setCreditpaymentdata((state) => ({
          ...state,
          payment: false,
          message: res?.message || t("Failed to create crypto payment"),
        }));
        return;
      }

      const data = res?.data || {};
      const nextTxId =
        data?.client_reference_id ||
        data?.order_id ||
        data?.transactionId ||
        data?.pid ||
        "";
      const nextCregisId = data?.cregis_id || "";
      const nextCheckoutUrl = data?.checkout_url || "";

      if (!nextCheckoutUrl) {
        setMessage(t("Checkout URL missing from Cregis response"));
        return;
      }

      transactionIdRef.current = nextTxId;
      cregisIdRef.current = nextCregisId;
      setTransactionId(nextTxId);
      setCregisId(nextCregisId);
      setCheckoutUrl(nextCheckoutUrl);
      setOrderStatus(STATUS.NEW);

      setCreditpaymentdata((state) => ({
        ...state,
        message: "",
        payment: false,
        submitdata: false,
        stripekey: nextCregisId || nextTxId,
        Paymentmethod_type: paymentdatashow?.Paymentmethod_type,
        payment_slag: paymentdatashow?.payment_slag || "cregis-payment",
        PaymentMethod: paymentdatashow?.PaymentMethod,
        PaymentMethod_id:
          paymentdatashow?.PaymentMethod_id || paymentdatashow?.id,
      }));

      if (data?.expire_time) {
        const remainingSec = Math.max(
          60,
          Math.floor((Number(data.expire_time) - Date.now()) / 1000)
        );
        setCountdown(remainingSec);
      } else if (data?.valid_time) {
        setCountdown(Number(data.valid_time) * 60);
      } else {
        setCountdown(DEFAULT_COUNTDOWN_SEC);
      }

      const win = openCheckoutUrl(nextCheckoutUrl);
      if (win) {
        checkoutWindowRef.current = win;
        setPopupBlocked(false);
        setMessage(
          t("Cregis checkout opened. Waiting for webhook payment confirmation...")
        );
      } else {
        setPopupBlocked(true);
        setMessage(
          t("Popup blocked. Click Open Cregis checkout, then wait for confirmation.")
        );
      }

      startPolling();
    } catch (err) {
      console.error("Ecommerce Cregis create error:", err);
      setMessage(t("Failed to create crypto payment"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
      closeCheckoutWindow();
    };
  }, [closeCheckoutWindow, stopPolling]);

  return (
    <div className="cregis-payment">
      {!transactionId ? (
        <button
          type="button"
          className="cregis-payment__start-btn"
          disabled={loading}
          onClick={createCregisOrder}
        >
          {loading ? t("Creating payment...") : t("Pay with Crypto")}
        </button>
      ) : (
        <div className="cregis-payment__panel">
          <div className="cregis-payment__meta">
            <div>
              <span className="cregis-payment__label">{t("Status")}</span>
              <strong
                className={`cregis-payment__status cregis-payment__status--${orderStatus}`}
              >
                {t(statusLabel(orderStatus))}
              </strong>
            </div>
            <div>
              <span className="cregis-payment__label">{t("Time remaining")}</span>
              <strong>{formatCountdown(countdown)}</strong>
            </div>
          </div>

          {(cregisId || transactionId) && (
            <div className="cregis-payment__ids">
              {cregisId ? (
                <p>
                  <span>{t("Cregis ID")}:</span> {cregisId}
                </p>
              ) : null}
              {transactionId ? (
                <p>
                  <span>{t("Order ref")}:</span> {transactionId}
                </p>
              ) : null}
            </div>
          )}

          <p className="cregis-payment__hint">
            {t(
              "Pay on the Cregis checkout page (QR shown there). This screen only waits for webhook confirmation."
            )}
          </p>

          <div className="cregis-payment__actions">
            <button
              type="button"
              className="cregis-payment__checkout-btn"
              onClick={reopenCheckout}
            >
              {popupBlocked
                ? t("Open Cregis checkout")
                : t("Reopen Cregis checkout")}
            </button>
            <button
              type="button"
              className="cregis-payment__refresh-btn"
              onClick={checkPaymentStatus}
            >
              {t("Refresh status")}
            </button>
          </div>

          {message ? <p className="cregis-payment__message">{message}</p> : null}
          {polling ? (
            <p className="cregis-payment__hint">
              {t("Listening for Cregis webhook → backend → PAID...")}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default EcommerceCregisPayment;
